import { BackButton, Spinner } from '@/components';
import {
    InsurancePolicyProviderDto,
    IUserDto,
    ProductDto,
    SelectChildDto,
} from '@/shared/interfaces';
import { BookOrQueueUp } from '../widgets/book-or-queue-up/BookOrQueueUp';
import { DescribeProblem } from '../widgets/describe-problem/DescribeProblem';
import { Disclaimer } from '../widgets/disclaimer/Disclaimer';
import { NotifyOwnDoctor } from '../widgets/notify-own-doctor/NotifyOwnDoctor';
import { PhoneForm } from '../widgets/phone/PhoneForm';
import { SelectCaregiver } from '../widgets/select-caregiver/SelectCaregiver';
import { SelectCategory } from '../widgets/select-category/SelectCategory';
import { SelectPatient } from '../widgets/select-patient/SelectPatient';
import { SelectPet } from '../widgets/select-pet/SelectPet';
import { SelectProduct } from '../widgets/select-product/SelectProduct';
import { R } from '@/features/booking/resources/Texts.resx';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
    isAdolescentEnabledAtom,
} from '@/caregiverSpace/shared/model';
import { useHydrateAtoms } from 'jotai/utils';
import { BookingPage } from '@/features/booking/shared/model/bookingState';
import { useBooking } from '@/features/booking/shared/model/useBooking';
import { BookingErrorModal } from '@/features/booking/shared/ui/BookingErrorModal';

export type BookingProps = {
    products: ProductDto[];
    showAddChildButton: boolean;
    showParent: boolean;
    initialChildren: SelectChildDto[];
    user: IUserDto;
    insuranceProviderDto?: InsurancePolicyProviderDto;
    isAdolescentEnabled: boolean;
    adolescentMinimumAge: number;
    adultMinimumAge: number;
};

export function Booking({
    products,
    showAddChildButton,
    showParent,
    initialChildren,
    user,
    insuranceProviderDto,
    isAdolescentEnabled,
    adolescentMinimumAge,
    adultMinimumAge,
}: BookingProps) {
    const {
        children,
        setChildren,
        currentPage,
        onAcceptDisclaimer,
        chosenProduct,
        productOptions,
        navigateBack,
        onSelectProduct,
        onSelectPatient,
        onSelectPet,
        caregiverTitle,
        onSubmitDescribeProblem,
        onSubmitCategory,
        onSubmitPreferredCaregiver,
        onSelectNotifyOwnDoctor,
        onSubmitPhone,
        isLoading,
    } = useBooking(products, user, initialChildren, insuranceProviderDto);

    useHydrateAtoms([
        [isAdolescentEnabledAtom, isAdolescentEnabled],
        [adolescentMinimumAgeAtom, adolescentMinimumAge],
        [adultMinimumAgeAtom, adultMinimumAge],
    ]);

    if (isLoading) {
        return (
            <div className="ns:bg-gray-100 flex h-4/5 w-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="content-grid ns:bg-gray-100 ns:px-4 mx-auto py-6">
            <div className="content-grid-narrow">
                <BackButton
                    onClick={navigateBack}
                    className="mb-4"
                    text={R.Back}
                />

                {currentPage === BookingPage.SelectPatient && (
                    <SelectPatient
                        onSelectPatient={onSelectPatient}
                        showAddChildButton={showAddChildButton}
                        showParent={showParent}
                        children={children}
                        setChildren={setChildren}
                        user={user}
                    />
                )}

                {currentPage === BookingPage.SelectPet && (
                    <SelectPet onSelectPet={onSelectPet} />
                )}

                {currentPage === BookingPage.SelectProduct && (
                    <SelectProduct
                        onSelectProduct={onSelectProduct}
                        caregiverTitle={caregiverTitle}
                        products={productOptions}
                        insuranceProviderName={insuranceProviderDto?.Name}
                        insuranceProviderLogoPath={
                            insuranceProviderDto?.LogoPathSmall
                        }
                        shouldHidePrice={
                            insuranceProviderDto?.HidePaidProducts ?? false
                        }
                    />
                )}

                {currentPage === BookingPage.Disclaimer && !!chosenProduct && (
                    <Disclaimer
                        disclaimer={chosenProduct.Texts.Disclaimer}
                        onAcceptDisclaimer={onAcceptDisclaimer}
                    />
                )}

                {currentPage === BookingPage.SelectCategory &&
                    !!chosenProduct && (
                        <SelectCategory
                            product={chosenProduct}
                            onSubmit={onSubmitCategory}
                        />
                    )}

                {currentPage === BookingPage.NotifyOwnDoctor && (
                    <NotifyOwnDoctor
                        onSelectNotifyOwnDoctor={onSelectNotifyOwnDoctor}
                    />
                )}

                {currentPage === BookingPage.DescribeProblem &&
                    !!chosenProduct && (
                        <DescribeProblem
                            onSubmit={onSubmitDescribeProblem}
                            chosenProduct={chosenProduct}
                        />
                    )}

                {currentPage === BookingPage.Phone && chosenProduct && (
                    <PhoneForm
                        user={user}
                        onSubmitPhone={onSubmitPhone}
                        product={chosenProduct}
                    />
                )}

                {currentPage === BookingPage.SelectCaregiver &&
                    !!chosenProduct && (
                        <SelectCaregiver
                            product={chosenProduct}
                            onSubmitCaregiver={onSubmitPreferredCaregiver}
                        />
                    )}

                {currentPage === BookingPage.BookOrQueueUp &&
                    !!chosenProduct && (
                        <BookOrQueueUp
                            product={chosenProduct}
                            userUid={user.UID}
                        />
                    )}

                <BookingErrorModal />
            </div>
        </div>
    );
}
